import React, { useState, forwardRef, useImperativeHandle, useId } from 'react';

const initialItemState = {
  description: '',
  unit: 'SQ FT',
  hsn: '',
  quantity: '',
  rate: '',
  amount: ''
};

const BillItem = forwardRef((props, ref) => {
  const [itemForm, setItemForm] = useState(initialItemState);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const formId = useId();

  const openAddModal = () => {
    setItemForm(initialItemState);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const openEditModal = (id) => {
    const itemToEdit = items.find(item => item.id === id);
    if (!itemToEdit) return;
    setItemForm({
      description: itemToEdit.description || '',
      unit: itemToEdit.unit || 'SQ FT',
      hsn: itemToEdit.hsn || '',
      quantity: itemToEdit.quantity ?? '',
      rate: itemToEdit.rate ?? '',
      amount: itemToEdit.amount ?? ''
    });
    setEditingId(id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setItemForm(initialItemState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'quantity' || name === 'rate') {
      const quantity = name === 'quantity' ? value : itemForm.quantity;
      const rate = name === 'rate' ? value : itemForm.rate;
      const calculatedAmount = quantity && rate
        ? (parseFloat(quantity) * parseFloat(rate)).toFixed(2)
        : '';

      setItemForm(prevState => ({
        ...prevState,
        [name]: value,
        amount: calculatedAmount
      }));
      return;
    }

    setItemForm(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSaveItem = (e) => {
    e.preventDefault();
    if (!itemForm.description || !itemForm.quantity || !itemForm.rate) {
      alert('Please fill description, quantity, and rate');
      return;
    }

    if (editingId !== null) {
      setItems(prev => prev.map(item =>
        item.id === editingId ? { ...itemForm, id: editingId } : item
      ));
    } else {
      setItems(prev => [...prev, { ...itemForm, id: Date.now() }]);
    }

    closeModal();
  };

  const handleRemoveItem = (id) => {
    if (!window.confirm('Remove this line item?')) return;
    setItems(prev => prev.filter(item => item.id !== id));
  };

  useImperativeHandle(ref, () => ({
    getItems: () => items,
    setItems: (newItems) => setItems(
      (newItems || []).map((item, index) => ({
        ...item,
        id: item.id ?? `loaded-${index}-${Date.now()}`
      }))
    )
  }));

  const formatMoney = (value) => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <>
      <section className="create-bill-section">
        <div className="create-bill-section-head">
          <div>
            <h2>Bill Items</h2>
            <p className="create-bill-section-hint">
              {items.length} item{items.length === 1 ? '' : 's'} on this invoice
            </p>
          </div>
          <button type="button" className="create-bill-btn" onClick={openAddModal}>
            Add item
          </button>
        </div>

        {items.length === 0 ? (
          <div className="create-bill-empty">
            <h3>No line items yet</h3>
            <p>Add items worked on to add to this bill.</p>
            <button type="button" className="create-bill-btn" onClick={openAddModal}>
              Add first item
            </button>
          </div>
        ) : (
          <div className="create-bill-table-wrap">
            <table className="create-bill-table">
              <thead>
                <tr>
                  <th className="col-sr">#</th>
                  <th className="col-desc">Description</th>
                  <th className="col-unit">Unit</th>
                  <th className="col-hsn">HSN</th>
                  <th className="col-num">Qty</th>
                  <th className="col-num">Rate</th>
                  <th className="col-num">Amount</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id}>
                    <td className="col-sr">{index + 1}</td>
                    <td className="col-desc">{item.description}</td>
                    <td className="col-unit">{item.unit}</td>
                    <td className="col-hsn">{item.hsn}</td>
                    <td className="col-num">{item.quantity}</td>
                    <td className="col-num">{formatMoney(item.rate)}</td>
                    <td className="col-num">{formatMoney(item.amount)}</td>
                    <td className="col-actions">
                      <div className="row-actions">
                        <button
                          type="button"
                          className="create-bill-btn-secondary small"
                          onClick={() => openEditModal(item.id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="create-bill-btn-danger small"
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {isModalOpen && (
        <div className="create-bill-modal-overlay" onClick={closeModal}>
          <div className="create-bill-modal" onClick={(e) => e.stopPropagation()}>
            <div className="create-bill-modal-head">
              <div>
                <h2>{editingId !== null ? 'Edit item' : 'Add item'}</h2>
                <p>Fill in the line item details. Amount is calculated automatically.</p>
              </div>
              <button type="button" className="create-bill-btn-ghost" onClick={closeModal}>
                Close
              </button>
            </div>

            <form id={formId} onSubmit={handleSaveItem}>
              <div className="create-bill-grid">
                <div className="create-bill-field span-2">
                  <label htmlFor={`${formId}-description`}>Description</label>
                  <textarea
                    id={`${formId}-description`}
                    name="description"
                    value={itemForm.description}
                    onChange={handleChange}
                    rows="3"
                    required
                    autoFocus
                    placeholder="Describe the work or material"
                  />
                </div>

                <div className="create-bill-field">
                  <label htmlFor={`${formId}-unit`}>Unit</label>
                  <select
                    id={`${formId}-unit`}
                    name="unit"
                    value={itemForm.unit}
                    onChange={handleChange}
                    required
                  >
                    <option value="SQ FT">SQ FT</option>
                    <option value="SQ MTR">SQ MTR</option>
                    <option value="NOS">NOS</option>
                  </select>
                </div>

                <div className="create-bill-field">
                  <label htmlFor={`${formId}-hsn`}>HSN</label>
                  <input
                    type="text"
                    id={`${formId}-hsn`}
                    name="hsn"
                    value={itemForm.hsn}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="create-bill-field">
                  <label htmlFor={`${formId}-quantity`}>Quantity</label>
                  <input
                    type="number"
                    id={`${formId}-quantity`}
                    name="quantity"
                    value={itemForm.quantity}
                    onChange={handleChange}
                    required
                    min="0"
                    step="any"
                  />
                </div>

                <div className="create-bill-field">
                  <label htmlFor={`${formId}-rate`}>Rate</label>
                  <input
                    type="number"
                    id={`${formId}-rate`}
                    name="rate"
                    value={itemForm.rate}
                    onChange={handleChange}
                    required
                    min="0"
                    step="any"
                  />
                </div>

                <div className="create-bill-field span-2">
                  <label htmlFor={`${formId}-amount`}>Amount</label>
                  <input
                    type="number"
                    id={`${formId}-amount`}
                    name="amount"
                    value={itemForm.amount}
                    readOnly
                  />
                </div>
              </div>
            </form>

            <div className="create-bill-modal-foot">
              <button type="button" className="create-bill-btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" form={formId} className="create-bill-btn">
                {editingId !== null ? 'Update item' : 'Add item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default BillItem;
